import { useCore } from '@/composables/core/index.js'
import { getAnalyticsToken, getAppsflyerUserId, getGoogleAnalyticsClientId } from '@/lib/analytics/helpers.js'
import { getPublicApiCallUrl } from '@/api/get-public-api-call-url.js'
import { isLocalhost, useLogger, useUuid, createBooksyApiClient, useBooksyApiClient } from '@booksy-com/vue-tools'
import { computed } from 'vue'
import axios from '@/api/axios.js'

const logger = useLogger('api-client')

export const createApiClient = () => {
  const { store } = useCore()
  const country = computed(() => store.state.app.country)
  const fingerprint = computed(() => store.state.app.fingerprint)
  const language = computed(() => store.getters['app/acceptLanguage'])
  const accessToken = computed(() => store.state.identity.accessToken)
  const businessId = computed(() => store.state.business.business.id)
  const uuid = useUuid()

  const config = {
    domain: window.location.origin,
    proxy: process.env.VUE_APP_PROXY_LOCALHOST
      || isLocalhost(window.location.origin),
    key: store.state.api.apiKey,
    axios,
    country,
    language,
    fingerprint,
    accessToken,
    appVersion: '3.0',
    bksReqId: () => uuid.v4(),
    userPseudoId: () => getGoogleAnalyticsClientId(),
    appsflyerUserId: () => getAppsflyerUserId(),
    analyticsTokens: () => getAnalyticsToken(),
    urlParams: {
      businessId,
    },
  }

  axios.interceptors.request.use(async (config) => {
    const { store } = useCore()
    const cfg = await store.dispatch('api/addHCaptcha', config)
    return cfg
  })

  return createBooksyApiClient(config)
}

export function useApiClient () {
  return useBooksyApiClient()
}

// this ensures const ENDPOINTS compatibility with Old API client
// new client calls do not use that and they expect API call URLs in new format
export const transformConfig = (config) => {
  const apiClient = useApiClient()
  const { store } = useCore()
  const publicApiUrl = store.state.api.publicApiUrl

  const cfg = { ...config }

  const apiVersion = config.apiVersion || store.state.api.apiVersion

  // transform URL for local public API call
  cfg.url = getPublicApiCallUrl(cfg.url, publicApiUrl)

  // replace :param to {param} for new client compatibility
  cfg.url = cfg.url.replace(/:([a-z0-9]+)(\/|$)/gi, '{$1}$2')

  // prefix urls with /core/v{apiVersion} for CORE endpoints
  if (!apiClient.detectService(cfg.url)) {
    cfg.url = `/core/v${apiVersion}${cfg.url}`
  }

  logger.debug('API call url transformed', config.url, '>>', cfg.url)

  return cfg
}
